import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const getRoot = () => document.getElementById('root');

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const isBack = useRef(false);
  const backScrollTop = useRef(0);
  // Tracks whether the last navigation was a back/forward so the post-paint
  // useEffect below doesn't clobber the restored scroll position.
  const wasBack = useRef(false);

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      isBack.current = true;
      backScrollTop.current = Number(
        (e.state as Record<string, unknown> | null)?.['_st'] ?? 0
      );
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useLayoutEffect(() => {
    if (hash) return;
    const root = getRoot();
    if (!root) return;

    if (isBack.current) {
      isBack.current = false;
      wasBack.current = true;
      root.scrollTop = backScrollTop.current;
      window.scrollTo(0, backScrollTop.current);
      return;
    }

    wasBack.current = false;
    root.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  // Post-paint belt-and-suspenders: some browsers drift the window scroll back
  // after the Suspense fallback swaps to real content (scroll anchoring). Fire a
  // second reset after the browser has painted to catch that case.
  useEffect(() => {
    if (hash) return;
    if (wasBack.current) return;
    window.scrollTo(0, 0);
    const root = getRoot();
    if (root) root.scrollTop = 0;
  }, [pathname, hash]);

  // iOS Safari restores scroll on overflow-y:auto containers asynchronously.
  // Cancel any scroll on #root for 200ms after forward navigation.
  useEffect(() => {
    if (hash) return;
    if (wasBack.current) return;

    const root = getRoot();
    if (!root) return;

    const reset = () => {
      root.scrollTop = 0;
    };
    root.addEventListener('scroll', reset, { passive: true });
    const stop = setTimeout(() => {
      root.removeEventListener('scroll', reset);
    }, 200);

    return () => {
      root.removeEventListener('scroll', reset);
      clearTimeout(stop);
    };
  }, [pathname, hash]);

  return null;
}
